<template>
  <div
    class="ticket-row ticket-name"
    :class="{ 'ticket-ordered': !!amount && !readOnly, disabled: inactive }"
  >
    <div class="">
      <slot name="icon">
        <Ticket />
      </slot>
    </div>
    <div>
      <span class="text-lg">{{ ticket.name }}</span>
      <div
        v-if="ticketDescription"
        class=""
      >
        <button
          v-if="canTruncateDescription"
          type="button"
          class="px-4 float-right"
          @click="toggleDescription"
        >
          <iconify-icon
            icon="mdi:chevron-up"
            class="text-2xl transition-transform"
            :class="{ '-rotate-180': descriptionTruncated }"
          />
        </button>
        <span
          class="text-vp-base-500"
          v-html="sanitizeHtml(description)"
        />
      </div>
    </div>
  </div>
  <div
    v-if="amount == 0 && ticket.soldOut"
    :class="{ disabled: inactive }"
    class="ticket-row quantity-buttons sold-out-label row-span-2 sm:row-span-1 sm:col-span-2 p-2 lg:p-4 flex text-2xl font-bold text-vp-base-900 justify-self-stretch justify-end border-r-4 border-r-transparent border-b border-b-vp-base-300"
  >
    {{ $gettext('Sold out') }}
  </div>
  <template v-else>
    <form
      v-if="!readOnly"
      class="ticket-row quantity-buttons p-2 lg:p-4 flex"
      :class="{ 'ticket-ordered': !!amount && !readOnly }"
    >
      <ButtonsNumericInput
        :value="desiredAmount"
        :disabled="disabled || inactive"
        :multiplier="ticket.multiple"
        :minimum="ticket.minimum"
        @set="(num: number) => setTicketAmount(num)"
        @add="(delta: number) => addTicketAmount(delta)"
      />
      <Accessible :description="$gettext('Add tickets you want to purchase')" />
    </form>

    <div
      v-if="readOnly"
      class="ticket-row quantity-buttons p-2 lg:p-4 flex justify-self-stretch"
    >
      {{ amount }}
    </div>

    <div
      class="ticket-row price"
      :class="{ 'ticket-ordered': !!amount && !readOnly, 'text-base': readOnly, disabled: inactive }"
    >
      <div class="text-right">
        {{ formatCurrency(ticket.breakdown.displayPrice) }}
      </div>
      <div
        v-if="includesFees && ticket.breakdown?.displayPrice > 0"
        class="text-right font-normal uppercase text-vp-base-500 text-xs sm:text-sm"
      >
        {{ $gettext('Incl. Fees') }}*
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import sanitizeHtml from 'sanitize-html';
import { formatCurrency, HARD_CODED_USD_CHANGE_ME } from '@/helpers/currency';
import ButtonsNumericInput from '@/components/ButtonsNumericInput.vue';
import Ticket from '@/components/icons/Ticket.vue';
import type { Checkout } from '@vp/common';
import { useCheckoutStore } from '@/stores/checkout';
import { LoadingState } from '@/util/LoadingState';
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import Accessible from '@/components/Accessible.vue';
import { useRecaptcha } from '../components/recaptcha/useRecaptcha';
import type { VPError } from '@/types/VPError';
import { useToast } from 'vue-toastification';
import { useDebounceFn } from '@vueuse/core';
import { useTruncatedHtml } from '@/composables/useTruncatedHtml';
import { useTracking } from '@/components/tracking/useTracking';

const recaptcha = useRecaptcha();
const checkoutStore = useCheckoutStore();
const toast = useToast();
const { trackEvent } = useTracking();

interface Props {
  ticket: Checkout.TicketBlueprint.CheckoutTicketBlueprint;
  includesFees?: boolean;
  readOnly?: boolean;

  // Temporary solution, until styling is fixed with css. Currently, readOnly causes numbers to be smaller
  inactive?: boolean;
}
const props = defineProps<Props>();

const amount = computed(
  () => checkoutStore.getCartedTickets.find(({ itemId }) => itemId === props.ticket.id)?.count || 0,
);
const desiredAmount = ref(amount.value);

const disabled = computed(() => checkoutStore.shoppingCartLoadingState == LoadingState.LOADING);

watch(
  () => [amount.value, disabled.value],
  ([newAmount, newLoading]) => {
    if (!newLoading) {
      desiredAmount.value = newAmount;
    }
  },
);

const setTicketAmount = async (num: number) => {
  desiredAmount.value = Math.max(0, num);
  updateTicketAmount();
};

const addTicketAmount = (delta: number) => {
  setTicketAmount(desiredAmount.value + delta);
};

const updateTicketAmount = useDebounceFn(async () => {
  if (desiredAmount.value == amount.value) {
    return;
  }

  const change = desiredAmount.value - amount.value;
  if (change > 0) {
    trackEvent('add_to_cart', {
      currency: HARD_CODED_USD_CHANGE_ME,
      items: [
        {
          item_id: props.ticket.id.toString(),
          item_name: props.ticket.name,
          quantity: change,
          price: props.ticket.breakdown?.displayPrice,
        },
      ],
    });
  } else {
    trackEvent('remove_from_cart', {
      currency: HARD_CODED_USD_CHANGE_ME,
      items: [
        {
          item_id: props.ticket.id.toString(),
          item_name: props.ticket.name,
          quantity: -change,
          price: props.ticket.breakdown?.displayPrice,
        },
      ],
    });
  }

  try {
    await checkoutStore.setTicketCount(props.ticket.id, desiredAmount.value, recaptcha);
  } catch (e: VPError | unknown) {
    if (e.type == 'VPError') {
      const vpError = e as VPError;
      for (const msg of vpError.errors) {
        toast.error(msg);
      }
      return;
    }

    throw e;
  }
}, 300);

const ticketDescription = computed(() => props.ticket.description);

const {
  text: description,
  truncate: descriptionTruncated,
  canTruncate: canTruncateDescription,
} = useTruncatedHtml(ticketDescription, {
  limit: 150,
  startTruncated: false,
  sanitizer: (input: string) => sanitizeHtml(input),
});

const toggleDescription = () => {
  descriptionTruncated.value = !descriptionTruncated.value;
};

onBeforeMount(() => {
  // In order for SSR to render the full description
  descriptionTruncated.value = true;
});

onMounted(() => {
  trackEvent('view_item', {
    currency: HARD_CODED_USD_CHANGE_ME,
    value: props.ticket.breakdown?.displayPrice,
    items: [
      {
        item_id: props.ticket.id.toString(),
        item_name: props.ticket.name,
        price: props.ticket.breakdown?.displayPrice,
      },
    ],
  });
});
</script>
